import { Breadcrumb, Button, message, notification, TableProps } from "antd";
import {
  getColumnSearchProps,
  getColumnSelectProps,
} from "../../../../shared/base-components/Filter/Filters";
import { useEffect, useState } from "react";
import CoaStyles from "./CoaList.module.css";
import CoaService from "../../services/CoaService";
import {
  COA_NAME_LABEL,
  CREATE_COA_BUTTON_LABEL,
  DEFAULT_GUID,
  FILTER_CREATED_BY_LABEL,
  FILTER_CREATED_ON_LABEL,
  NAME_KEY,
  NO_OF_DELIVERABLES_LABEL,
  TOAST_DEFAULT_ERROR_MESSAGE,
} from "../../../../shared/utils/CommonConstants";
import { CreateModal } from "../../../../shared/screen-components/CommonCreateComponent/CommanCreateComponent";
import { Coa, CoaFilter, CoaFilterParams } from "./CoaList.types";
import { useNavigate } from "react-router-dom";
import { Container } from "../../../../shared/base-components/Layouts/Container/Container.styles";
import { PagedResponse } from "../../../../shared/types/PagedResponse";
import { Flex } from "../../../../shared/base-components/Layouts/Flex/Flex.styles";
import { SearchInput } from "../../../../shared/base-components/Search/Search";
import { Table } from "../../../../shared/base-components/Table/Table";

export const CoaList: React.FC = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [coa, setCoa] = useState<Coa[]>([]);
  const [filterOptions, setFilterOptions] = useState<CoaFilter>({
    createdUserList: [],
  });

  const [templateNameFilter, setTemplateNameFilter] = useState<string | null>(
    null
  );
  const [createdByFilter, setCreatedByFilter] = useState<string | null>(null);
  const [createdOnFilter, setCreatedOnFilter] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(false);

  const [total, setTotal] = useState(0);
  const [searchValue, setSearchValue] = useState<string>("");

  const columns: TableProps["columns"] = [
    {
      title: COA_NAME_LABEL,
      dataIndex: "templateName",
      ...getColumnSearchProps(
        "templateName",
        COA_NAME_LABEL,
        templateNameFilter,
        setTemplateNameFilter
      ),
      width: "25%",
    },
    {
      title: FILTER_CREATED_BY_LABEL,
      dataIndex: "createdByUserName",
      ...getColumnSelectProps(
        "createdBy",
        FILTER_CREATED_BY_LABEL,
        filterOptions.createdUserList.map((t) => {
          return { value: t.id, label: t.userName };
        }),
        createdByFilter,
        setCreatedByFilter,
        !!createdByFilter && createdByFilter !== DEFAULT_GUID
      ),
      width: "25%",
    },
    {
      title: FILTER_CREATED_ON_LABEL,
      dataIndex: "createdOn",
      ...getColumnSearchProps(
        "createdOn",
        FILTER_CREATED_ON_LABEL,
        createdOnFilter,
        setCreatedOnFilter,
        true
      ),
      width: "25%",
    },
    {
      title: NO_OF_DELIVERABLES_LABEL,
      dataIndex: "deliverableCount",
      width: "25%",
    },
  ];

  useEffect(() => {
    fetchFilterOptions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData(1, 10);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, templateNameFilter, createdByFilter, createdOnFilter]);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const coaService = new CoaService();

  const fetchData = async (
    currentPage: number,
    pageSize: number
  ): Promise<void> => {
    setIsLoading(true);
    try {
      // Construct filterObject
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const selectedCreatedBy = filterOptions.createdUserList.find(
        (createdBy) => createdBy.userName === createdByFilter
      );

      const date = createdOnFilter ? new Date(createdOnFilter) : null;
      const formattedDate = date
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
            2,
            "0"
          )}-${String(date.getDate()).padStart(2, "0")}`
        : null;

      const filterObject: CoaFilterParams = {
        templateName: templateNameFilter || "",
        createdBy: createdByFilter ?? DEFAULT_GUID,
        createdOn: formattedDate || null,
        startIndex: currentPage - 1,
        itemsPerPage: pageSize,
        searchValue: searchValue ?? null,
      };

      const response = await coaService.getAllCoA<PagedResponse<Coa[]>>(
        filterObject
      );

      setCoa(response.items);
      setTotal(response.totalCount);
    } catch (error) {
      notification.error({ message: TOAST_DEFAULT_ERROR_MESSAGE });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFilterOptions = async (): Promise<void> => {
    try {
      const response: CoaFilter = await coaService.coaFilter();
      setFilterOptions(response);
    } catch (error) {
      notification.error({ message: TOAST_DEFAULT_ERROR_MESSAGE });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const clearFilter = () => {
    if (!!templateNameFilter || !!createdByFilter || !!createdOnFilter) {
      setTemplateNameFilter(null);
      setCreatedOnFilter(null);
      setCreatedByFilter(null);
    }
  };

  const openCoA = async (id: number, name: string) => {
    try {
      await coaService.getCoaSheetData(id);
      navigate(`/coa/${id}?${NAME_KEY}=${name}`);
    } catch (error) {
      message.error(TOAST_DEFAULT_ERROR_MESSAGE);
    }
  };

  return (
    <Container>
      <Breadcrumb
        className={CoaStyles.breadCrump}
      />
      <div className="extra">
        <Flex gap="12px">
          <SearchInput
            onSearch={(value) => setSearchValue(value)}
            placeholder="Search CoA"
          />
          {/* <Button id="coa-clear-filter" onClick={clearFilter}>Clear Filter</Button> */}
        </Flex>
        <Button id="create-coa" type="primary" onClick={() => showModal()}>
          {CREATE_COA_BUTTON_LABEL}
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={coa}
        isLoading={isLoading}
        fetchData={fetchData}
        total={total}
        onRow={(record) => {
          return {
            onClick: () => openCoA(record.coaId, record.templateName),
          };
        }}
      />
      <CreateModal
        isVisible={isModalOpen}
        isTemplate={false}
        fetchData={fetchData}
        onCancel={() => setIsModalOpen(false)}
      />
    </Container>
  );
};
